import React from 'react';
import { Link } from 'gatsby';

import './PostList.css';

const POSTS_PER_BATCH = 10;

class PostList extends React.Component {
  state = { visibleCount: POSTS_PER_BATCH };

  sentinelRef = React.createRef();

  componentDidMount() {
    window.addEventListener('scroll', this.handleScroll, { passive: true });
    // Fill the grid container height before the user needs to scroll
    this.fillTimeout = setTimeout(() => this.fillGrid(), 50);
  }

  componentDidUpdate(prevProps, prevState) {
    const { posts } = this.props;
    const { visibleCount } = this.state;
    const loadedMore = prevState.visibleCount !== visibleCount;
    const postsChanged = prevProps.posts !== posts;
    if (visibleCount < posts.length && (loadedMore || postsChanged)) {
      // Cancel any pending timer before scheduling a new one
      clearTimeout(this.fillTimeout);
      this.fillTimeout = setTimeout(() => this.fillGrid(), 50);
    }
  }

  componentWillUnmount() {
    window.removeEventListener('scroll', this.handleScroll);
    clearTimeout(this.fillTimeout);
  }

  // Walk up to find the grid container (the element that sets page height via sidebar)
  getGridElement = () => {
    let elem = this.sentinelRef.current;
    while (elem) {
      if (elem.classList && elem.classList.contains('blogPosts__grid')) return elem;
      elem = elem.parentElement;
    }
    return null;
  };

  // Load more posts until the sentinel reaches the bottom of the grid container,
  // after which only scroll-triggered loading is needed.
  fillGrid = () => {
    const sentinel = this.sentinelRef.current;
    if (!sentinel) return;
    const { posts } = this.props;
    const { visibleCount } = this.state;
    if (visibleCount >= posts.length) return;

    const grid = this.getGridElement();
    if (grid) {
      const gridBottom = grid.getBoundingClientRect().bottom;
      const sentinelBottom = sentinel.getBoundingClientRect().bottom;
      // Keep loading while the sentinel hasn't reached the grid bottom
      if (sentinelBottom < gridBottom - 50) {
        this.loadMore();
        return;
      }
    }

    // Grid is filled — check if user scrolled near the sentinel for further loading
    this.handleScroll();
  };

  handleScroll = () => {
    const sentinel = this.sentinelRef.current;
    if (!sentinel) return;
    const { posts } = this.props;
    const { visibleCount } = this.state;
    if (visibleCount >= posts.length) return;
    const { bottom } = sentinel.getBoundingClientRect();
    if (bottom <= window.innerHeight + 300) {
      this.loadMore();
    }
  };

  loadMore = () => {
    const { posts } = this.props;
    this.setState(prev => ({
      visibleCount: Math.min(prev.visibleCount + POSTS_PER_BATCH, posts.length)
    }));
  };

  render() {
    const { posts, activeCategory } = this.props;
    const { visibleCount } = this.state;
    const visiblePosts = posts.slice(0, visibleCount);
    const hasMore = visibleCount < posts.length;

    return (
      <ul className="postList">
        {visiblePosts.map(({ node: post }) => {
          const title = post.frontmatter.title || post.fields.slug;
          const categories = post.frontmatter.categories;
          const category =
            activeCategory && categories && categories.includes(activeCategory)
              ? activeCategory
              : categories && categories.length > 0 ? categories[0] : null;
          const date = post.frontmatter.date;

          return (
            <li key={post.fields.slug} className="postList__item">
              <Link to={post.fields.slug} className="postList__link">
                <span className="postList__title">{title}</span>
                <span className="postList__meta">
                  {category && (
                    <span className="postList__badge">{category}</span>
                  )}
                  {date && <span className="postList__date">{date}</span>}
                </span>
              </Link>
            </li>
          );
        })}
        {hasMore && (
          <li
            ref={this.sentinelRef}
            className="postList__sentinel"
            aria-hidden="true"
          />
        )}
      </ul>
    );
  }
}

export default PostList;
